import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Mail, ShoppingCart } from 'lucide-react';

import { Button, NamedLink } from '..';
import { LinkedLogo } from '../../components';
import IconSearchDesktop from '../../containers/TopbarContainer/Topbar/TopbarSearchForm/IconSearchDesktop';
import TopbarSearchForm from '../../containers/TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import SearchIcon from './SearchIcon';
import UserProfile from './UserProfile';

import { useIsMobile } from '../../hooks/useIsMobile';
import { parse, stringify } from '../../util/urlHelpers';
import css from './Navbar.module.css';

const Navbar = props => {
  const {
    config,
    customLinks,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showCreateListingsLink,
    showSearchForm,
    inboxTab,
    history,
    location,
    onStartSellingClick,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCompactSearchRange, setIsCompactSearchRange] = useState(false);
  const [isCompactSearchOpen, setIsCompactSearchOpen] = useState(false);
  const compactSearchRef = useRef(null);
  const cartItemCount = useSelector(state => state.cart?.cartItems.length);

  const { isMobile } = useIsMobile();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px) and (max-width: 1279px)');
    const updateCompactSearchRange = event => {
      const inRange = event.matches;
      setIsCompactSearchRange(inRange);
      if (!inRange) {
        setIsCompactSearchOpen(false);
      }
    };

    updateCompactSearchRange(mediaQuery);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateCompactSearchRange);
    } else {
      mediaQuery.addListener(updateCompactSearchRange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateCompactSearchRange);
      } else {
        mediaQuery.removeListener(updateCompactSearchRange);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCompactSearchOpen) {
      return undefined;
    }

    const handleClickOutside = event => {
      if (compactSearchRef.current && !compactSearchRef.current.contains(event.target)) {
        setIsCompactSearchOpen(false);
      }
    };

    const handleEsc = event => {
      if (event.key === 'Escape') {
        setIsCompactSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isCompactSearchOpen]);

  useEffect(() => {
    if (!isCompactSearchOpen || !compactSearchRef.current) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      const input = compactSearchRef.current?.querySelector('input');
      input?.focus();
    }, 120);

    return () => window.clearTimeout(focusTimer);
  }, [isCompactSearchOpen]);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;

  const redirectToURLWithModalState = (history, location, modalStateParam) => {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
    history.push(`${pathname}${searchString}`, state);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isStartSellingLink = link => link?.route?.name === 'EditListingPage';

  const handleStartSellingClick = () => {
    closeMenu();
    if (onStartSellingClick) {
      onStartSellingClick();
    }
  };

  const FullDesktopSearch = (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  );

  const CompactDesktopSearch = (
    <div
      ref={compactSearchRef}
      className={classNames(css.compactSearchShell, {
        [css.compactSearchOpen]: isCompactSearchOpen,
      })}
    >
      {isCompactSearchOpen ? (
        <TopbarSearchForm
          className={classNames(css.searchLink, css.compactSearchForm)}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        />
      ) : (
        <button
          type="button"
          className={css.compactSearchToggle}
          onClick={() => setIsCompactSearchOpen(true)}
          aria-label={intl.formatMessage({ id: 'Topbar.searchIcon' })}
          title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
        >
          <IconSearchDesktop />
        </button>
      )}
    </div>
  );

  const SearchButton = showSearchForm ? (
    isMobile ? (
      <Button
        rootClassName={css.iconButton}
        onClick={() => {
          location.search = '';
          redirectToURLWithModalState(history, location, 'mobilesearch');
        }}
        title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
      >
        <SearchIcon
          className={css.searchMenuIcon}
          ariaLabel={intl.formatMessage({ id: 'Topbar.searchIcon' })}
        />
      </Button>
    ) : isCompactSearchRange ? (
      CompactDesktopSearch
    ) : (
      FullDesktopSearch
    )
  ) : null;

  return (
    <nav className={css.navbar}>
      {/* Desktop & Mobile Header */}
      <div className={css.navbarHeader}>
        <LinkedLogo
          rootClassName={css.logoLink}
          logoClassName={css.logoRoot}
          logoImageClassName={css.logoImage}
          layout={'desktop'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {/* Desktop Navigation */}
        <div className={css.desktopNav}>
          <CustomLinksMenu
            currentPage={currentPage}
            currentPathname={location?.pathname}
            customLinks={customLinks}
            intl={intl}
            hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
            showCreateListingsLink={showCreateListingsLink}
            isAuthenticated={isAuthenticated}
            onStartSellingClick={onStartSellingClick}
          />
        </div>

        {/* Icons Container */}
        <div className={css.iconsContainer}>
          {/* Search Icon */}
          {SearchButton}

          {/* Inbox Icon (Desktop Only) */}
          {authenticatedOnClientSide && !isMobile ? (
            <NamedLink
              name="InboxPage"
              params={{ tab: inboxTab }}
              title={intl.formatMessage({ id: 'TopbarDesktop.inbox' })}
            >
              <button className={css.iconButton2} aria-label="Messages">
                <Mail className={css.icon} />
                {notificationCount > 0 ? <div className={css.notificationDot} /> : null}
              </button>
            </NamedLink>
          ) : null}

          {/* Profile Icon */}
          <UserProfile
            authenticatedOnClientSide={authenticatedOnClientSide}
            showCreateListingsLink={showCreateListingsLink}
            currentPage={currentPage}
            onLogout={onLogout}
            size={18}
          />

          {/* Shopping Cart */}
          {/* {authenticatedOnClientSide ? (
            <NamedLink name="ShoppingCartPage">
              <button className={css.iconButton2} aria-label="Shopping cart">
                <ShoppingCart className={css.icon} />
                {cartItemCount > 0 && (
                  <span className={css.badge}>{cartItemCount > 99 ? '99+' : cartItemCount}</span>
                )}
              </button>
            </NamedLink>
          ) : null} */}

          {/* Hamburger Menu (Mobile Only) */}
          <button
            className={css.hamburgerButton}
            onClick={toggleMenu}
            aria-label="Menu"
            aria-expanded={isMenuOpen}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <aside className={css.mobileMenu}>
          {/* Mobie Menu Header */}
          <header className={css.mobileMenuHeader}>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoRoot}
              logoImageClassName={css.logoImage}
              layout={'mobile'}
              alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
            <div className={classNames(css.iconsContainer, css.mobileMenuIcons)}>
              {/* Profile Icon in Menu */}
              <UserProfile
                authenticatedOnClientSide={authenticatedOnClientSide}
                showCreateListingsLink={showCreateListingsLink}
                currentPage={currentPage}
                onLogout={onLogout}
                size={18}
              />

              {/* Close Button */}
              <button
                className={classNames(css.hamburgerButton, css.closeButton)}
                onClick={closeMenu}
                aria-label="Close menu"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </header>

          {/* Mobile Menu Content */}
          <div className={css.mobileMenuContent}>
            <div className={css.mobileMenuCustomLinks}>
              {customLinks.map((link, index) => {
                if (!isAuthenticated && isStartSellingLink(link)) {
                  return (
                    <button
                      key={`mobile-navbar-item-${index}`}
                      type="button"
                      className={`${css.mobileMenuLink} ${css.mobileMenuLinkButton}`}
                      onClick={handleStartSellingClick}
                    >
                      <span>{link.text}</span>
                    </button>
                  );
                }

                if (link.type === 'internal' && link.route) {
                  const { name, params, to } = link.route || {};
                  return (
                    <NamedLink
                      key={`mobile-navbar-item-${index}`}
                      name={name}
                      params={params}
                      to={to}
                      className={css.mobileMenuLink}
                    >
                      <span>{link.text}</span>
                    </NamedLink>
                  );
                } else {
                  return (
                    <a
                      key={`mobile-navbar-item-${index}`}
                      href={link.href || '/'}
                      className={css.mobileMenuLink}
                      onClick={closeMenu}
                    >
                      {link.text}
                    </a>
                  );
                }
              })}
            </div>

          </div>
        </aside>
      )}
    </nav>
  );
};

export default Navbar;
